import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, concatMap, debounceTime, switchMap, withLatestFrom } from 'rxjs/operators';
import { removeTab, setActiveTab, changeActiveSideTab, changeActiveTab, addTab, setUniverseSelection, replaceAllTabs } from './action';
import { AppState, companySideNavList, themeSideNavList, StoredTabData, Tab } from './app.state';
import { TabsService } from '../features/results/services/tabs.service';




@Injectable()
export class TabEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private tabsService: TabsService
  ) {}

  // Effect to handle tab removal and state transition
  removeTab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeTab),
      withLatestFrom(this.store.select('main'), this.store.select('TabList')),
      switchMap(([action, main, tabList]) => {
        const { activeTabId } = main;
        const remainingTabs = tabList.filter(tab => tab.tabId !== action.tabId);

        if (remainingTabs.length === 0) {
          // No tabs are left, navigate to the home page
          this.router.navigate(['/home']);
          return [];
        } else if (activeTabId === action.tabId) {
          // If the removed tab was the active one, set the next available tab as active
          const nextTab = remainingTabs[0];
          const fallbackSideTab = (nextTab.search_type === 'company' ? companySideNavList : themeSideNavList)[0];
          return [
            setActiveTab({ tab: nextTab }),
            changeActiveTab({ tabId: nextTab.tabId }),
            changeActiveSideTab({ sideTab: fallbackSideTab })
          ];
        }
        return [];
      })
    )
  );



  //effect to make sure the active tabs has a proper active sidetab
  validateActiveSideTab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeActiveTab, setActiveTab, replaceAllTabs),
      withLatestFrom(this.store.select('TabList'), this.store.select('main')),
      switchMap(([action, tabs, main]) => {
        const tabId = action.type === changeActiveTab.type ? action.tabId : main.activeTabId;
        const activeTab = tabs.find(tab => tab.tabId === tabId);
        if (!activeTab) {
          return of();
        }
        const allowedSideTabs = activeTab.search_type === 'company' ? companySideNavList : themeSideNavList;
        if (allowedSideTabs.includes(main.activeSideTab)) {
          return of();
        }
        return of(changeActiveSideTab({ sideTab: allowedSideTabs[0] }));
      })
    )
  );

  // Create tab in DB when added
  createTabInDb$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addTab),
      concatMap(({ tab }) => {
        const payload: StoredTabData = {
          tabId: tab.tabId,
          search_type: tab.search_type,
          params: tab.params,
          tab_name: tab.tab_name,
          universe: tab.universe
        };
        return this.tabsService.createTab(payload);
      })
    ),
    { dispatch: false }
  );

  // Delete tab from DB when removed
  deleteTabFromDb$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeTab),
      concatMap(({ tabId }) => {
        return this.tabsService.deleteTab(tabId).pipe(
          catchError(err => {
            console.error(`Failed to delete tab ${tabId}:`, err);
            return of(undefined);
          })
        );
      })
    ),
    { dispatch: false }
  );

  // Update tab in DB when universe changes (debounced)
  updateTabInDb$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setUniverseSelection),
      debounceTime(300),
      withLatestFrom(this.store.select('TabList')),
      switchMap(([action, tabList]) => {
        const tab = tabList.find(t => t.tabId === action.tabId);
        if (!tab) {
          return of(undefined);
        }
        const payload: StoredTabData = {
          tabId: tab.tabId,
          search_type: tab.search_type,
          params: tab.params,
          tab_name: tab.tab_name,
          universe: tab.universe
        };
        return this.tabsService.updateTab(action.tabId, payload);
      })
    ),
    { dispatch: false }
  );
}
